import React, { memo } from 'react';
import { Check, Bookmark, Target, ChevronRight, Building2, Users } from 'lucide-react';
import { SignalSuggestion } from '../../types';
import { ConfidenceDots } from './ConfidenceDots';

interface SignalCardProps {
  signal: SignalSuggestion;
  isSelected: boolean;
  isSaved: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onSave: (e: React.MouseEvent) => void;
  onExpandToggle: (e: React.MouseEvent) => void;
}

/**
 * Card component for displaying a signal suggestion.
 * Includes selection checkbox, save button, and expandable criteria section.
 */
export const SignalCard = memo<SignalCardProps>(({
  signal,
  isSelected,
  isSaved,
  isExpanded,
  onToggle,
  onSave,
  onExpandToggle,
}) => {
  const isPeople = signal.target_entity === 'people';

  return (
    <div
      className={`rounded-xl border transition-all ${
        isSelected
          ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {/* Main card - clickable for selection */}
      <div onClick={onToggle} className="p-3.5 cursor-pointer">
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              isSelected ? 'bg-gray-900 border-gray-900' : 'border-gray-300'
            }`}
          >
            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="text-[13px] font-semibold text-gray-900">{signal.name}</h4>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSave(e);
                  }}
                  className={`p-1 rounded transition-colors ${
                    isSaved
                      ? 'text-amber-500 hover:text-amber-600'
                      : 'text-gray-300 hover:text-gray-500'
                  }`}
                  title={isSaved ? 'Remove from saved' : 'Save for later'}
                >
                  <Bookmark
                    className="w-3.5 h-3.5"
                    fill={isSaved ? 'currentColor' : 'none'}
                  />
                </button>
                <ConfidenceDots priority={signal.priority} />
                <span className={`flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${
                  isPeople ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'
                }`}>
                  {isPeople ? <Users className="w-2.5 h-2.5" /> : <Building2 className="w-2.5 h-2.5" />}
                </span>
              </div>
            </div>
            <p className="text-[12px] text-gray-600 leading-relaxed">{signal.description}</p>
            {signal.rationale && (
              <p className="text-[11px] text-gray-400 mt-1.5">
                <span className="font-medium">Why:</span> {signal.rationale}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Expandable criteria section */}
      {signal.search_criteria && signal.search_criteria.length > 0 && (
        <div className={`border-t ${isExpanded ? 'border-gray-200 bg-gray-50/50' : 'border-gray-100'}`}>
          <button
            onClick={onExpandToggle}
            className={`w-full px-3.5 py-2 text-[11px] flex items-center justify-between transition-all ${
              isExpanded
                ? 'text-gray-600 font-medium'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Target className="w-3 h-3" />
              {isExpanded ? 'Search Criteria' : `${signal.search_criteria.length} search criteria`}
            </span>
            <ChevronRight
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            />
          </button>

          {/* Animated expanded content */}
          <div
            className={`overflow-hidden transition-all duration-200 ease-out ${
              isExpanded ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3.5 pb-3.5">
              <div className="space-y-1.5">
                {signal.search_criteria.map((criterion, cidx) => (
                  <div
                    key={cidx}
                    className="flex items-start gap-2.5 py-1.5 px-2.5 bg-white rounded-lg border border-gray-100"
                  >
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium flex items-center justify-center">
                      {cidx + 1}
                    </span>
                    <span className="text-[12px] text-gray-700 leading-relaxed">{criterion}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

SignalCard.displayName = 'SignalCard';

export default SignalCard;
